import React from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setPageantFlow,
  setFootballFlow,
  isPageantBanner,
  PAGEANT_SECTION_NAME,
} from "../../../utils/homeFlow";
import footballChooserImg from "../../../assets/banners/home-banner-football.png";
import pageantChooserImg from "../../../assets/banners/home-banner-pageant.png";
import footballLogo from "../../../assets/images2/chooser-logo-football.png";
import pageantLogo from "../../../assets/images2/chooser-logo-pageant.png";

function bannerImageUrl(banner, pageant) {
  const url = banner?.banner_img;
  if (url && String(url).trim()) return url;
  return pageant ? pageantChooserImg : footballChooserImg;
}

function bannerLabel(banner, pageant) {
  if (pageant) return PAGEANT_SECTION_NAME;
  return (
    banner?.homeBannerCategory?.name || banner?.category_name || "Football"
  );
}

function bannerTitle(banner, pageant) {
  if (pageant) return PAGEANT_SECTION_NAME;
  return (
    banner?.banner_name ||
    banner?.homeBannerCategory?.name ||
    "Goal Stream"
  );
}

/**
 * Two-banner entry chooser — image, title, and order from admin Home Banners.
 */
function HomeBannerChooser({ onSelectExisting }) {
  const navigate = useNavigate();
  const chooser = useSelector((state) => state.homeBannerChooser);
  const banners = Array.isArray(chooser?.data?.data)
    ? chooser.data.data.slice(0, 2)
    : [];

  const handleClick = (banner, index) => {
    const pageant =
      isPageantBanner(banner) ||
      (!banners.some(isPageantBanner) && index === 1);
    if (pageant) {
      setPageantFlow();
      navigate("/competitions");
      return;
    }
    setFootballFlow();
    onSelectExisting?.();
  };

  if (chooser.loading) {
    return (
      <Box
        sx={{
          minHeight: { xs: "72vh", md: "82vh" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: { xs: 2, md: 4 },
          py: 3,
        }}
      >
        <Skeleton
          width={220}
          height={28}
          sx={{ bgcolor: "rgba(255,255,255,0.08)", mb: 2, mx: "auto" }}
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2.5,
          }}
        >
          {[0, 1].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              sx={{
                height: { xs: 240, sm: 320, md: 460 },
                borderRadius: 4,
                bgcolor: "rgba(255,255,255,0.08)",
              }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  if (!banners.length) {
    return null;
  }

  return (
    <Box
      className="home-banner-chooser"
      sx={{
          minHeight: { xs: "auto", md: "82vh" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: { xs: 1.5, sm: 2, md: 4 },
          py: { xs: 2, md: 4 },
          width: "100%",
          overflowX: "hidden",
          boxSizing: "border-box",
      }}
    >
      <Box sx={{ textAlign: "center", mb: { xs: 2.5, md: 3.5 } }}>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.55)",
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 600,
            fontSize: 12,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          Welcome
        </Typography>
        <Typography
          sx={{
            color: "#fff",
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 800,
            fontSize: { xs: 24, sm: 28, md: 40 },
            letterSpacing: "-0.03em",
            lineHeight: 1.15,
          }}
        >
          Choose your experience
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.6)",
            mt: 1,
            fontSize: { xs: 13, sm: 15 },
            px: { xs: 1.5, sm: 2 },
            maxWidth: 440,
            mx: "auto",
            lineHeight: 1.5,
          }}
        >
          Pick Football or {PAGEANT_SECTION_NAME} to continue. You can switch anytime.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: 2, md: 2.5 },
          maxWidth: 1100,
          mx: "auto",
          width: "100%",
          alignItems: "stretch",
        }}
      >
        {banners.map((banner, index) => {
          const pageant =
            isPageantBanner(banner) ||
            (!banners.some(isPageantBanner) && index === 1);
          const label = bannerLabel(banner, pageant);
          const title = bannerTitle(banner, pageant);
          const imgSrc = bannerImageUrl(banner, pageant);
          return (
            <Box
              key={banner.id || index}
              onClick={() => handleClick(banner, index)}
              sx={{
                position: "relative",
                width: "100%",
                height: { xs: 240, sm: 320, md: 460 },
                minHeight: { xs: 240, sm: 320, md: 460 },
                borderRadius: 4,
                overflow: "hidden",
                cursor: "pointer",
                isolation: "isolate",
                boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: "0 32px 70px rgba(0,0,0,0.55)",
                },
                "&:hover .chooser-cta": {
                  background: "#ef364e",
                  color: "#fff",
                },
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  handleClick(banner, index);
              }}
            >
              <Box
                component="img"
                src={imgSrc}
                alt=""
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(8,10,18,0.15) 0%, rgba(8,10,18,0.35) 40%, rgba(8,10,18,0.92) 100%)",
                }}
              />
              <Box
                className="home-banner-chooser-logo"
                sx={{
                  position: "absolute",
                  top: { xs: 10, md: 14 },
                  right: { xs: 10, md: 14 },
                  zIndex: 2,
                  width: { xs: 118, sm: 132, md: 148 },
                  height: { xs: 112, sm: 124, md: 138 },
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "flex-end",
                  pointerEvents: "none",
                }}
              >
                <Box
                  component="img"
                  src={pageant ? pageantLogo : footballLogo}
                  alt=""
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    display: "block",
                    filter: "drop-shadow(0 3px 10px rgba(0,0,0,0.7))",
                  }}
                />
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  p: { xs: 1.75, sm: 2.5, md: 3.5 },
                }}
              >
                {!pageant ? (
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 600,
                      fontSize: 12,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      mb: 0.8,
                    }}
                  >
                    {label}
                  </Typography>
                ) : null}
                <Typography
                  sx={{
                    color: "#fff",
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: pageant ? 600 : 800,
                    fontSize: pageant
                      ? { xs: 15, sm: 17, md: 20 }
                      : { xs: 22, sm: 26, md: 34 },
                    letterSpacing: pageant ? "0.01em" : "-0.02em",
                    lineHeight: 1.25,
                    mb: 2,
                    textTransform: "none",
                    maxWidth: pageant ? { xs: "100%", md: "92%" } : undefined,
                  }}
                >
                  {title}
                </Typography>
                <Box
                  className="chooser-cta"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 2,
                    py: 0.9,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#ef364e",
                    background: "rgba(255,255,255,0.95)",
                    transition: "all 0.2s ease",
                  }}
                >
                  Explore →
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default HomeBannerChooser;
